import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare,
  AlertCircle,
  FileText,
  Users,
  DollarSign,
  Settings,
  Filter,
  Search,
  Plus,
  MoreVertical,
  Calendar,
  User,
  Building,
  Loader2,
} from 'lucide-react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { ThemeToggle } from '../ui/ThemeToggle';
import { DeleteConfirmation } from '../ui/DeleteConfirmation';
import TaskModal from './TaskModal';

const AdminTasksBoard = () => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const toast = useToast();

  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(firestore, 'tasks'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snapshot => {
        setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setIsLoading(false);
        setConnectionStatus('connected');
      },
      err => {
        toast.error('Failed to load tasks. Retrying...', 'Connection Error');
        setConnectionStatus('disconnected');
        setIsLoading(false);
        setTimeout(() => {
          setConnectionStatus('reconnecting');
        }, 3000);
      }
    );
    return () => unsub();
  }, [toast]);

  const getTaskIconStyles = variant => {
    const styles = {
      primary: 'bg-primary/10 text-primary dark:bg-primary/20',
      warning: 'bg-warning/10 text-warning dark:bg-warning/20',
      info: 'bg-info/10 text-info dark:bg-info/20',
      error: 'bg-error/10 text-error dark:bg-error/20',
      success: 'bg-success/10 text-success dark:bg-success/20',
    };
    return styles[variant] || styles.primary;
  };

  const taskConfig = {
    approval: {
      icon: CheckSquare,
      variant: 'primary',
    },
    maintenance: {
      icon: Settings,
      variant: 'warning',
    },
    review: {
      icon: FileText,
      variant: 'info',
    },
    security: {
      icon: AlertCircle,
      variant: 'error',
    },
    training: {
      icon: Users,
      variant: 'success',
    },
    financial: {
      icon: DollarSign,
      variant: 'primary',
    },
  };

  const priorityVariants = {
    high: 'error',
    medium: 'warning',
    low: 'success',
  };

  const statusVariants = {
    pending: 'info',
    'in-progress': 'primary',
    completed: 'success',
  };

  const filters = [
    { value: 'all', label: 'All Tasks' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'high', label: 'High Priority' },
  ];

  const filteredTasks = tasks.filter(task => {
    const matchesFilter = filter === 'all' || task.status === filter || task.priority === filter;
    const matchesSearch =
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await updateDoc(doc(firestore, 'tasks', taskId), { status: newStatus });
      toast.success('Task status updated successfully');
    } catch (err) {
      toast.error('Failed to update task status', 'Error');
    }
  };

  const handleDeleteClick = task => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'tasks', taskToDelete.id));
      toast.success('Task deleted successfully');
      setShowDeleteModal(false);
      setTaskToDelete(null);
    } catch (err) {
      toast.error('Failed to delete task', 'Error');
    }
  };

  const handleEdit = task => {
    setEditTask(task);
    setShowTaskModal(true);
  };

  const handleAdd = () => {
    setEditTask(null);
    setShowTaskModal(true);
  };

  const handleModalClose = () => {
    setShowTaskModal(false);
    setEditTask(null);
  };

  const handleModalSave = async taskData => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const adminInfo = {
        adminId: currentUser.uid || 'unknown',
        adminEmail: currentUser.email || 'unknown',
        lastModified: new Date(),
        modifiedBy: currentUser.email || 'unknown',
      };

      if (editTask) {
        await updateDoc(doc(firestore, 'tasks', editTask.id), {
          ...taskData,
          ...adminInfo,
          updatedAt: new Date(),
        });
        toast.success('Task updated successfully');
      } else {
        await addDoc(collection(firestore, 'tasks'), {
          ...taskData,
          ...adminInfo,
          createdAt: new Date(),
          createdBy: currentUser.email || 'unknown',
        });
        toast.success('Task created successfully');
      }
      setShowTaskModal(false);
      setEditTask(null);
    } catch (err) {
      toast.error('Failed to save task. Please try again.', 'Error');
    }
  };

  const getTimeUntilDue = dueDate => {
    if (!dueDate) return 'No due date';
    const now = new Date();
    const due = dueDate instanceof Date ? dueDate : dueDate?.toDate?.() || new Date(dueDate);
    const diff = due - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (diff < 0) return 'Overdue';
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return 'Due soon';
  };

  const isOverdue = dueDate => {
    if (!dueDate) return false;
    const due = dueDate instanceof Date ? dueDate : dueDate?.toDate?.() || new Date(dueDate);
    return new Date() > due;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const taskVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  if (isLoading) {
    return (
      <Card className="mx-auto w-full max-w-6xl">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-6">
            <div className="flex items-center justify-between">
              <div className="h-8 w-48 rounded-lg bg-muted-background" />
              <div className="h-10 w-32 rounded-lg bg-muted-background" />
            </div>
            <div className="space-y-4">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="rounded-lg border border-border p-4">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted-background" />
                    <div className="flex-1 space-y-3">
                      <div className="h-5 w-3/4 rounded bg-muted-background" />
                      <div className="h-4 w-full rounded bg-muted-background" />
                      <div className="flex gap-2">
                        <div className="h-6 w-20 rounded-full bg-muted-background" />
                        <div className="h-6 w-24 rounded-full bg-muted-background" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="mx-auto w-full max-w-6xl"
    >
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary p-2.5">
                <CheckSquare className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <CardTitle>Admin Tasks Board</CardTitle>
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        connectionStatus === 'connected'
                          ? 'animate-pulse bg-success'
                          : connectionStatus === 'reconnecting'
                            ? 'animate-pulse bg-warning'
                            : 'bg-error'
                      }`}
                    />
                    <span className="text-xs text-text-tertiary">
                      {connectionStatus === 'connected'
                        ? 'Synced'
                        : connectionStatus === 'reconnecting'
                          ? 'Reconnecting...'
                          : 'Disconnected'}
                    </span>
                  </div>
                </div>
                <CardDescription className="mt-1">
                  Manage action items and pending approvals • Real-time sync between admins
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button onClick={handleAdd} leftIcon={<Plus className="h-4 w-4" />}>
                Add Task
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
              <Input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
              <Filter className="h-4 w-4 flex-shrink-0 text-text-tertiary" />
              {filters.map(filterOption => (
                <Button
                  key={filterOption.value}
                  variant={filter === filterOption.value ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilter(filterOption.value)}
                  className="whitespace-nowrap"
                >
                  {filterOption.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Tasks List */}
          <div className="max-h-[60vh] space-y-3 overflow-y-auto md:max-h-[500px]">
            <AnimatePresence mode="wait">
              {filteredTasks.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center"
                >
                  <CheckSquare className="mx-auto mb-4 h-12 w-12 text-text-tertiary" />
                  <p className="text-text-secondary">No tasks found</p>
                  {searchTerm && (
                    <p className="mt-2 text-sm text-text-tertiary">
                      Try adjusting your search or filter criteria
                    </p>
                  )}
                </motion.div>
              ) : (
                filteredTasks.map(task => {
                  const taskType = taskConfig[task.type] || taskConfig.approval;
                  const TaskIcon = taskType.icon;
                  const priorityVariant = priorityVariants[task.priority] || 'default';
                  const statusVariant = statusVariants[task.status] || 'default';

                  return (
                    <motion.div
                      key={task.id}
                      variants={taskVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={`group rounded-lg border border-border bg-surface p-4 transition-all hover:shadow-md ${
                        isOverdue(task.dueDate) ? 'border-error/50 bg-error/5 dark:bg-error/10' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`rounded-lg p-2.5 ${getTaskIconStyles(taskType.variant)}`}>
                          <TaskIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <h4 className="font-semibold text-text-primary">{task.title}</h4>
                            <div className="flex flex-shrink-0 items-center gap-2">
                              <Badge variant={priorityVariant} size="sm">
                                {task.priority}
                              </Badge>
                              <Badge variant={statusVariant} size="sm">
                                {task.status}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(task)}
                                className="h-8 w-8 p-0"
                                aria-label="Edit task"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(task)}
                                className="h-8 w-8 p-0 text-error hover:bg-error/10 hover:text-error"
                                aria-label="Delete task"
                              >
                                <AlertCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {task.description && (
                            <p className="mb-3 line-clamp-2 text-sm text-text-secondary">
                              {task.description}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap items-center gap-4 text-xs text-text-tertiary">
                              {task.assignee && (
                                <span className="flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5" />
                                  {task.assignee}
                                </span>
                              )}
                              {task.dueDate && (
                                <span className="flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {getTimeUntilDue(task.dueDate)}
                                </span>
                              )}
                              {task.attachments && (
                                <span className="flex items-center gap-1.5">
                                  <FileText className="h-3.5 w-3.5" />
                                  {task.attachments} files
                                </span>
                              )}
                              {task.comments && (
                                <span className="flex items-center gap-1.5">
                                  <Building className="h-3.5 w-3.5" />
                                  {task.comments} comments
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {task.status === 'pending' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateTaskStatus(task.id, 'in-progress')}
                                >
                                  Start
                                </Button>
                              )}
                              {task.status === 'in-progress' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateTaskStatus(task.id, 'completed')}
                                  className="border-success text-success hover:bg-success/10"
                                >
                                  Complete
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>

          {/* Stats Footer */}
          <div className="border-t border-border pt-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-text-primary">
                  {tasks.filter(t => t.status === 'pending').length}
                </p>
                <p className="text-sm text-text-tertiary">Pending</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">
                  {tasks.filter(t => t.status === 'in-progress').length}
                </p>
                <p className="text-sm text-text-tertiary">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-success">
                  {tasks.filter(t => t.status === 'completed').length}
                </p>
                <p className="text-sm text-text-tertiary">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-error">
                  {tasks.filter(t => isOverdue(t.dueDate)).length}
                </p>
                <p className="text-sm text-text-tertiary">Overdue</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <TaskModal
        open={showTaskModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        task={editTask}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        itemName={taskToDelete?.title}
      />
    </motion.div>
  );
};

export default AdminTasksBoard;
